import { ReactElement, useState } from "react";
import {
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Divider,
  Button,
} from "@mui/material";

export interface ClusteringFormProps {
  onSubmit?: (data: ClusteringData) => void;
  onBack?: () => void;
  onContinue?: () => void;
  className?: string;
}

export interface ClusteringData {
  resolution: number;
  flavor: string;
  n_iterations: number;
}

const ClusteringForm = ({
  onSubmit,
  onBack,
  onContinue,
  className = "",
}: ClusteringFormProps): ReactElement => {
  const [formData, setFormData] = useState<ClusteringData>({
    resolution: 1.0,
    flavor: "leidenalg",
    n_iterations: -1,
  });

  const handleInputChange = (
    field: keyof ClusteringData,
    value: string | number,
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value as any,
    }));
  };

  const handleSubmit = () => {
    onSubmit?.(formData);
  };

  return (
    <div
      className={`p-6 h-full flex flex-col justify-between ${className}`}
      style={{
        backgroundColor: "#F5F5F5", // tokens.colors.gray[100]
      }}
    >
      {/* Description */}
      <Box>
        <Typography
          variant="body2"
          className="text-gray-600 mb-4 leading-relaxed"
          sx={{ fontSize: "0.875rem", lineHeight: 1.6 }}
        >
          Clustering groups cells with similar gene expression profiles
          together, helping identify distinct cell types or states. The Leiden
          algorithm is commonly used for single-cell data as it produces
          high-quality clusters and is computationally efficient.
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          className="mb-6 leading-relaxed"
          sx={{ fontSize: "0.875rem", lineHeight: 1.6 }}
        >
          Configure the clustering parameters:
        </Typography>
      </Box>

      <Divider className="mb-6" />

      {/* Form Fields */}
      <div className="space-y-6 flex-1">
        <TextField
          fullWidth
          label="Resolution"
          type="number"
          value={formData.resolution}
          onChange={(e) => {
            const value = e.target.value;
            if (value === "") {
              handleInputChange("resolution", 0);
            } else {
              const numValue = parseFloat(value);
              if (!isNaN(numValue) && numValue >= 0) {
                handleInputChange("resolution", numValue);
              }
            }
          }}
          inputProps={{
            step: 0.01,
            min: 0,
          }}
          helperText="Higher values lead to more clusters (default: 1.0)"
          sx={{
            backgroundColor: "#FFFFFF",
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: "#D9D9D9",
              },
              "&:hover fieldset": {
                borderColor: "#19C1DB",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#19C1DB",
              },
            },
          }}
        />

        <FormControl fullWidth>
          <InputLabel id="flavor-label">Flavor</InputLabel>
          <Select
            labelId="flavor-label"
            value={formData.flavor}
            label="Flavor"
            onChange={(e) => handleInputChange("flavor", e.target.value)}
            sx={{
              backgroundColor: "#FFFFFF",
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "#D9D9D9",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "#19C1DB",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#19C1DB",
              },
            }}
          >
            <MenuItem value="leidenalg">Leidenalg</MenuItem>
            <MenuItem value="igraph">Igraph</MenuItem>
          </Select>
        </FormControl>

        <TextField
          fullWidth
          label="Number of Iterations"
          type="number"
          value={formData.n_iterations}
          onChange={(e) => {
            const value = e.target.value;
            if (value === "") {
              handleInputChange("n_iterations", 0);
            } else {
              const numValue = parseInt(value);
              if (!isNaN(numValue)) {
                handleInputChange("n_iterations", numValue);
              }
            }
          }}
          inputProps={{
            step: 1,
          }}
          helperText="Number of iterations (-1 for until convergence)"
          sx={{
            backgroundColor: "#FFFFFF",
            "& .MuiOutlinedInput-root": {
              "& fieldset": {
                borderColor: "#D9D9D9",
              },
              "&:hover fieldset": {
                borderColor: "#19C1DB",
              },
              "&.Mui-focused fieldset": {
                borderColor: "#19C1DB",
              },
            },
          }}
        />
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between gap-3 mt-6">
        <Button
          variant="outlined"
          color="primary"
          onClick={onBack}
          sx={{
            textTransform: "none",
            fontWeight: 500,
            px: 3,
            py: 1.5,
          }}
        >
          Back
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          className="bg-[#19C1DB] hover:bg-[#17B1CB] text-white"
          sx={{
            textTransform: "none",
            fontWeight: 500,
            px: 3,
            py: 1.5,
          }}
        >
          Submit
        </Button>
      </div>
    </div>
  );
};

export default ClusteringForm;
